import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';

export interface User {
  id: string;
  email: string;
  displayName: string;
  createdAt?: string;
}

export class AuthError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
  if (init?.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch { /* ignore parse errors */ }
    throw new AuthError(res.status, message);
  }
  return res.json() as Promise<T>;
}

export async function checkSession(): Promise<User | null> {
  try {
    const data = await jsonFetch<{ user: User }>('/api/auth/session');
    return data.user;
  } catch (err) {
    if (err instanceof AuthError && err.status === 401) return null;
    throw err;
  }
}

// Signed-in only: the server enrols the session's user, so no email is sent.
export async function passkeyRegisterStart(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  return jsonFetch<PublicKeyCredentialCreationOptionsJSON>(
    '/api/auth/passkey/register/start',
    { method: 'POST' }
  );
}

export async function passkeyRegisterFinish(
  response: RegistrationResponseJSON
): Promise<void> {
  await jsonFetch<{ ok: true }>('/api/auth/passkey/register/finish', {
    method: 'POST',
    body: JSON.stringify(response),
  });
}

export async function passkeyLoginStart(
  email: string
): Promise<PublicKeyCredentialRequestOptionsJSON> {
  return jsonFetch<PublicKeyCredentialRequestOptionsJSON>(
    '/api/auth/passkey/login/start',
    { method: 'POST', body: JSON.stringify({ email }) }
  );
}

export async function passkeyLoginFinish(
  response: AuthenticationResponseJSON
): Promise<void> {
  await jsonFetch<{ ok: true }>('/api/auth/passkey/login/finish', {
    method: 'POST',
    body: JSON.stringify(response),
  });
}

// Always resolves for a well-formed email: the server never says whether it has an account.
export async function requestSignInCode(email: string): Promise<void> {
  await jsonFetch<{ ok: true }>('/api/auth/code/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifySignInCode(email: string, code: string): Promise<User> {
  const data = await jsonFetch<{ ok: true; user: User }>('/api/auth/code/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
  return data.user;
}

export async function logout(): Promise<void> {
  await jsonFetch<{ ok: true }>('/api/auth/logout', { method: 'POST' });
}
